import React, { useEffect, useState } from 'react';
import type { Venue } from '../../../types/venue';
import { fetchVisitTimeline } from '../../../utils/venueApi';

interface GuestAnalyticsPanelProps {
  venue: Venue;
}

interface TimelineEntry {
  date: string;
  visits: number;
  uniqueGuests: number;
}

const PERIODS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

const GuestAnalyticsPanel: React.FC<GuestAnalyticsPanelProps> = ({ venue }) => {
  const [period, setPeriod] = useState('7d');
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchVisitTimeline(venue.id, period)
      .then((data) => setTimeline(Array.isArray(data) ? data : []))
      .catch(() => setTimeline([]))
      .finally(() => setLoading(false));
  }, [venue.id, period]);

  const maxVisits = Math.max(1, ...timeline.map((t) => t.visits));
  const totalVisits = timeline.reduce((sum, t) => sum + t.visits, 0);
  const totalUnique = timeline.reduce((sum, t) => sum + (t.uniqueGuests || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow mb-1.5">Guest Analytics</div>
          <h2 className="text-display text-2xl text-coffee-900">Visit timeline</h2>
        </div>
        <div className="flex gap-1 p-1 bg-coffee-50 border border-coffee-100 rounded-xl flex-shrink-0">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
                period === p.key
                  ? 'bg-coffee-900 text-white'
                  : 'text-coffee-500 hover:text-coffee-800'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-coffee-100 rounded-xl p-4">
          <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-1">Total visits</p>
          <p className="text-2xl font-mono text-coffee-900">{totalVisits}</p>
        </div>
        <div className="bg-white border border-coffee-100 rounded-xl p-4">
          <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-1">Unique guests</p>
          <p className="text-2xl font-mono text-coffee-900">{totalUnique}</p>
        </div>
      </div>

      {/* Bar chart */}
      {loading ? (
        <div className="h-40 bg-coffee-50 border border-coffee-100 rounded-xl animate-pulse" />
      ) : timeline.length === 0 ? (
        <div className="h-40 flex items-center justify-center bg-coffee-50 border border-coffee-100 rounded-xl">
          <div className="text-center">
            <p className="text-sm font-medium text-coffee-700">No visit data yet</p>
            <p className="text-xs text-coffee-500 mt-1">Data will appear once guests start connecting.</p>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-coffee-100 rounded-xl p-5">
          <div className="flex items-end gap-1 h-32" role="img" aria-label="Visit timeline bar chart">
            {timeline.map((entry) => (
              <div key={entry.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] font-mono text-coffee-500">{entry.visits}</span>
                <div
                  className="w-full bg-coffee-600 rounded-t-sm transition-all"
                  style={{ height: `${(entry.visits / maxVisits) * 100}%`, minHeight: entry.visits > 0 ? 4 : 0 }}
                  title={`${entry.date}: ${entry.visits} visits`}
                />
                <span className="text-[8px] text-coffee-400 truncate max-w-full">
                  {entry.date.split('-').slice(1).join('/')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestAnalyticsPanel;
